import { addMonths, format, isValid, parseISO } from "date-fns";

export type SaleInstallmentDraft = {
  installment_no: number;
  due_date: string;
  amount: number;
};

export type SaleInstallmentStatus = "pending" | "partially_paid" | "paid" | "overdue";

export const INSTALLMENT_MONTH_OPTIONS = [1, 2, 3, 6, 12] as const;

export function buildInstallmentPlan(
  balance: number,
  count: number,
  firstDueDate: string,
): SaleInstallmentDraft[] {
  const total = Math.round(Math.max(0, balance) * 100) / 100;
  const n = Math.max(1, Math.floor(count));
  if (total <= 0 || n < 1) return [];

  const start = parseISO(firstDueDate);
  if (!isValid(start)) return [];

  const base = Math.floor((total * 100) / n) / 100;
  let remaining = total;
  const rows: SaleInstallmentDraft[] = [];

  for (let i = 0; i < n; i += 1) {
    const isLast = i === n - 1;
    const amount = isLast ? Math.round(remaining * 100) / 100 : base;
    remaining = Math.round((remaining - amount) * 100) / 100;
    rows.push({
      installment_no: i + 1,
      due_date: format(addMonths(start, i), "yyyy-MM-dd"),
      amount,
    });
  }

  return rows;
}

export function defaultFirstInstallmentDueDate(saleDate: string, payableDate: string): string {
  const fromPayable = payableDate.trim();
  if (fromPayable) {
    const d = parseISO(fromPayable);
    if (isValid(d)) return format(d, "yyyy-MM-dd");
  }
  const fromSale = saleDate.trim();
  const base = fromSale && isValid(parseISO(fromSale)) ? parseISO(fromSale) : new Date();
  return format(addMonths(base, 1), "yyyy-MM-dd");
}

export function installmentRemaining(row: { amount: number; paid_amount?: number; remaining_amount?: number }): number {
  if (row.remaining_amount != null && Number.isFinite(Number(row.remaining_amount))) {
    return Math.max(0, roundMoney(Number(row.remaining_amount)));
  }
  const amount = Number(row.amount) || 0;
  const paid = Number(row.paid_amount) || 0;
  return Math.max(0, roundMoney(amount - paid));
}

function roundMoney(n: number): number {
  return Math.round(n * 100) / 100;
}

export function sumInstallmentAmounts(rows: SaleInstallmentDraft[]): number {
  return roundMoney(rows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0));
}

export function installmentsMatchBalance(
  rows: SaleInstallmentDraft[],
  balance: number,
  tolerance = 0.02,
): boolean {
  return Math.abs(sumInstallmentAmounts(rows) - roundMoney(balance)) <= tolerance;
}

export function installmentStatusLabel(status: SaleInstallmentStatus): string {
  switch (status) {
    case "paid":
      return "Paid";
    case "partially_paid":
      return "Partially paid";
    case "overdue":
      return "Overdue";
    default:
      return "Unpaid";
  }
}

export function installmentStatusClass(status: SaleInstallmentStatus): string {
  switch (status) {
    case "paid":
      return "bg-emerald-500/20 text-emerald-700 dark:text-emerald-400";
    case "partially_paid":
      return "bg-sky-500/20 text-sky-700 dark:text-sky-400";
    case "overdue":
      return "bg-destructive/15 text-destructive";
    default:
      return "bg-amber-500/20 text-amber-700 dark:text-amber-400";
  }
}
